import { useEffect, useState, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { ENVIRONMENT_VARIABLES, IS_NON_PROD } from 'src/constants/EnvironmentConstants';
import { appSyncOperationName, eLDAPGroup } from 'src/constants/AppConstants';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { logger } from 'src/logger';
import { recordApiRequest } from 'src/services/aws/CloudWatchRumService';
import { getCurrentTime, getTimeDifference } from 'src/utilities/DateTimeUtilities';
import {
  listConnectionsLeaders,
  getConnectionsScorecard,
  listConnectionsScorecards,
  getConnectionsTrends,
  listConnectionsFreetextTopics,
  listConnectionsQuestionResponses,
  getConnectionsHeadcount,
  listConnectionsFreetextResponses
} from './connectionsQueries';
import {
  ConnectionsLeader,
  ConnectionsScorecard,
  ConnectionsTrendPoint,
  ConnectionsFreetextTopic,
  ConnectionsQuestionResponse,
  ConnectionsHeadcountPoint,
  ConnectionsFreetextResponse,
  Sentiment
} from './types';

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  /** Identity of the request this data belongs to (e.g. "leader|startDate").
   *  Lets consumers detect a stale render where the data is for a prior leader. */
  key?: string;
}

const DEFAULT_STATE = <T>(): AsyncState<T> => ({ data: null, loading: false, error: null });

/**
 * Wraps fetchGraphQLRequest with timing/RUM tracking and consistent error handling.
 */
async function fetchConnections<T>(query: string, variables: Record<string, unknown>, requestName: string): Promise<T> {
  const start = getCurrentTime();
  try {
    // Connections resolvers derive access tier from the verified custom:LDAP_GROUPS
    // claim, which is ONLY present in the Cognito ID token. Amplify sends the ACCESS
    // token by default for userPool auth, so we send the ID token explicitly. As of the
    // alpha Cognito migration, EVERY stage (alpha and local dev included) runs the main
    // GraphQL API with a USER_POOL authorizer, so these userPool requests are authorized
    // on all stages. The useUserPool guard is retained defensively: if AuthenticationType
    // is ever set back to a non-userPool mode, we won't send a userPool request the API
    // cannot validate.
    const useUserPool = ENVIRONMENT_VARIABLES.env.AuthenticationType === 'userPool';
    const idToken = useUserPool ? (await fetchAuthSession()).tokens?.idToken?.toString() : undefined;
    const authOverride = idToken ? { authMode: 'userPool' as const, authToken: idToken } : {};
    const response: any = await fetchGraphQLRequest({
      query,
      variables,
      requestName,
      requestType: appSyncOperationName.QUERY,
      ...authOverride
    });
    const elapsed = getTimeDifference(start);
    logger.info(`Fetched ${requestName} in ${elapsed} ms`);
    recordApiRequest(requestName, elapsed);
    return response[requestName] as T;
  } catch (error: any) {
    logger.error(`Failed to fetch ${requestName}`, error);
    throw error;
  }
}

/**
 * Derives the access-control context sent with every Connections query.
 * In beta/prod the resolver re-derives both from the verified token (these are
 * ignored); in alpha (API_KEY, no identity) the resolver demo-trusts them.
 * isPoweruser = membership in the canary poweruser group, or the DEV override in
 * non-prod stages only (dev/alpha) -- devs do not get see-all in beta/prod.
 */
export function useViewerContext(): { viewerLogin: string; isPoweruser: boolean } {
  const auth = useAuth();
  const viewerLogin = auth.Alias || '';
  const powerGroups = IS_NON_PROD
    ? [eLDAPGroup.CANARY_SCORECARD_POWERUSERS_LDAP, eLDAPGroup.DEV_LDAP]
    : [eLDAPGroup.CANARY_SCORECARD_POWERUSERS_LDAP];
  const isPoweruser = powerGroups.some((g) => auth.userLDAPGroups?.includes(g));
  return { viewerLogin, isPoweruser };
}

export function useConnectionsLeaders(includeInactive = false): AsyncState<ConnectionsLeader[]> {
  const [state, setState] = useState<AsyncState<ConnectionsLeader[]>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();

  useEffect(() => {
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetchConnections<ConnectionsLeader[]>(listConnectionsLeaders, { includeInactive, viewerLogin, isPoweruser }, 'listConnectionsLeaders')
      .then((data) => {
        if (!cancelled) setState({ data: data || [], loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [includeInactive, viewerLogin, isPoweruser]);

  return state;
}

export function useConnectionsScorecard(
  leaderLogin: string | null,
  metricPeriod: string | null,
  startDate?: string | null,
  scope?: string,
  summaryType?: string
): AsyncState<ConnectionsScorecard> {
  const [state, setState] = useState<AsyncState<ConnectionsScorecard>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();

  useEffect(() => {
    if (!leaderLogin || !metricPeriod) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetchConnections<ConnectionsScorecard>(
      getConnectionsScorecard,
      { leaderLogin, metricPeriod, startDate, scope, summaryType, viewerLogin, isPoweruser },
      'getConnectionsScorecard'
    )
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: null, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [leaderLogin, metricPeriod, startDate, scope, summaryType, viewerLogin, isPoweruser]);

  return state;
}

export function useConnectionsScorecards(
  metricPeriod: string | null,
  groupByDimension: string,
  scope?: string,
  summaryType?: string,
  leaderLogin?: string | null,
  startDate?: string | null
): AsyncState<ConnectionsScorecard[]> {
  const [state, setState] = useState<AsyncState<ConnectionsScorecard[]>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();

  useEffect(() => {
    if (!metricPeriod) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetchConnections<ConnectionsScorecard[]>(
      listConnectionsScorecards,
      { leaderLogin, metricPeriod, startDate, groupByDimension, scope, summaryType, viewerLogin, isPoweruser },
      'listConnectionsScorecards'
    )
      .then((data) => {
        // Stamp the data with the leader|startDate it was fetched for so consumers
        // (AI summary) can reject a stale render where focusLeader has already moved on.
        if (!cancelled) setState({ data: data || [], loading: false, error: null, key: `${leaderLogin ?? ''}|${startDate ?? ''}` });
      })
      .catch((error) => {
        // Stamp the key on error too, else a failed fetch leaves a stale/undefined key and the
        // AI summary's leaderMatches guard stays false (stuck spinner) with no recovery.
        if (!cancelled) setState({ data: [], loading: false, error, key: `${leaderLogin ?? ''}|${startDate ?? ''}` });
      });
    return () => {
      cancelled = true;
    };
  }, [metricPeriod, groupByDimension, scope, summaryType, leaderLogin, startDate, viewerLogin, isPoweruser]);

  return state;
}

export function useConnectionsTrends(
  leaderLogin: string | null,
  theme: string,
  scope?: string,
  summaryType?: string
): AsyncState<ConnectionsTrendPoint[]> {
  const [state, setState] = useState<AsyncState<ConnectionsTrendPoint[]>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();

  useEffect(() => {
    if (!leaderLogin) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetchConnections<ConnectionsTrendPoint[]>(
      getConnectionsTrends,
      { leaderLogin, theme, scope, summaryType, viewerLogin, isPoweruser },
      'getConnectionsTrends'
    )
      .then((data) => {
        if (!cancelled) setState({ data: data || [], loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [leaderLogin, theme, scope, summaryType, viewerLogin, isPoweruser]);

  return state;
}

export function useConnectionsFreetextTopics(
  leaderLogin: string | null,
  metricPeriod: string | null,
  startDate?: string | null
): AsyncState<ConnectionsFreetextTopic[]> {
  const [state, setState] = useState<AsyncState<ConnectionsFreetextTopic[]>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();

  useEffect(() => {
    if (!leaderLogin || !metricPeriod) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetchConnections<ConnectionsFreetextTopic[]>(
      listConnectionsFreetextTopics,
      { leaderLogin, metricPeriod, startDate, viewerLogin, isPoweruser },
      'listConnectionsFreetextTopics'
    )
      .then((data) => {
        if (!cancelled) setState({ data: data || [], loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [leaderLogin, metricPeriod, startDate, viewerLogin, isPoweruser]);

  return state;
}

/**
 * Raw verbatim free-text responses for one leader/period. Powers the word cloud
 * + drill-down. startDate (the period's START month, YYYY-MM-01) is REQUIRED by the
 * resolver (bounds the result under the RDS Data API 1MB limit), so we skip the fetch
 * when it's absent. metricPeriod == MONTHLY_TRAILING_THREE_MONTHS unions startDate + the
 * 2 FOLLOWING months (e.g. Apr -> Apr/May/Jun, deduped); otherwise just startDate's month.
 */
export function useConnectionsFreetextResponses(
  leaderLogin: string | null,
  metricPeriod: string | null,
  startDate?: string | null
): AsyncState<ConnectionsFreetextResponse[]> {
  const [state, setState] = useState<AsyncState<ConnectionsFreetextResponse[]>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();

  useEffect(() => {
    if (!leaderLogin || !metricPeriod || !startDate) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetchConnections<ConnectionsFreetextResponse[]>(
      listConnectionsFreetextResponses,
      { leaderLogin, metricPeriod, startDate, viewerLogin, isPoweruser },
      'listConnectionsFreetextResponses'
    )
      .then((data) => {
        if (!cancelled) setState({ data: data || [], loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [leaderLogin, metricPeriod, startDate, viewerLogin, isPoweruser]);

  return state;
}

export function useConnectionsQuestionResponses(
  leaderLogin: string | null,
  metricPeriod: string | null,
  startDate?: string | null
): AsyncState<ConnectionsQuestionResponse[]> {
  const [state, setState] = useState<AsyncState<ConnectionsQuestionResponse[]>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();

  useEffect(() => {
    if (!leaderLogin || !metricPeriod) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetchConnections<ConnectionsQuestionResponse[]>(
      listConnectionsQuestionResponses,
      { leaderLogin, metricPeriod, startDate, viewerLogin, isPoweruser },
      'listConnectionsQuestionResponses'
    )
      .then((data) => {
        if (!cancelled) setState({ data: data || [], loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [leaderLogin, metricPeriod, startDate, viewerLogin, isPoweruser]);

  return state;
}

export interface MultiLeaderFreetextRow {
  leaderLogin: string;
  leaderName: string;
  topicId: string;
  topicName: string;
  sentiment: Sentiment;
  responseCount: number | null;
  metricValue: number | null;
  sampleSize: number | null;
}

/**
 * Fans out the per-leader freetext query across a set of leaders (typically the
 * selected leader + their directs) and flattens every topic into a
 * leader x topic row. Powers the "Sentiment Deep Dive" charts on the Insights
 * tab, where each bar is "<Leader> · <Topic>" sized by response count.
 *
 * Per-leader failures are swallowed (best-effort) so one bad fetch doesn't blank
 * the whole view. The dep key is the sorted login list so the effect only
 * re-runs when the leader set, period, or start date actually changes.
 */
export function useMultiLeaderFreetext(
  leaders: Array<{ login: string; name: string }>,
  metricPeriod: string | null,
  startDate?: string | null
): AsyncState<MultiLeaderFreetextRow[]> {
  const [state, setState] = useState<AsyncState<MultiLeaderFreetextRow[]>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();
  const key = leaders
    .map((l) => l.login)
    .sort()
    .join(',');

  useEffect(() => {
    if (leaders.length === 0 || !metricPeriod) {
      setState({ data: [], loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });

    Promise.all(
      leaders.map((leader) =>
        fetchConnections<ConnectionsFreetextTopic[]>(
          listConnectionsFreetextTopics,
          { leaderLogin: leader.login, metricPeriod, startDate, viewerLogin, isPoweruser },
          'listConnectionsFreetextTopics'
        )
          .then((topics) =>
            (topics || []).map((t) => ({
              leaderLogin: leader.login,
              leaderName: leader.name,
              topicId: t.topicId,
              topicName: t.topicName,
              sentiment: t.sentiment,
              responseCount: t.responseCount,
              metricValue: t.metricValue,
              sampleSize: t.sampleSize
            }))
          )
          .catch(() => [] as MultiLeaderFreetextRow[])
      )
    )
      .then((perLeader) => {
        if (cancelled) return;
        setState({ data: perLeader.flat(), loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, metricPeriod, startDate, viewerLogin, isPoweruser]);

  return state;
}

/**
 * Per-leader headcount/attrition time series (long/tidy). One row per
 * metricName x metricMonth for the given leader, from getConnectionsHeadcount.
 * Monthly, scenario='Actuals' -- independent of the scorecard cadence.
 */
export function useConnectionsHeadcount(
  leaderLogin: string | null,
  startDate?: string | null,
  endDate?: string | null
): AsyncState<ConnectionsHeadcountPoint[]> {
  const [state, setState] = useState<AsyncState<ConnectionsHeadcountPoint[]>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();

  useEffect(() => {
    if (!leaderLogin) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetchConnections<ConnectionsHeadcountPoint[]>(
      getConnectionsHeadcount,
      { leaderLogin, startDate, endDate, viewerLogin, isPoweruser },
      'getConnectionsHeadcount'
    )
      .then((data) => {
        if (!cancelled) setState({ data: data || [], loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [leaderLogin, startDate, endDate, viewerLogin, isPoweruser]);

  return state;
}

/**
 * Fans out the per-leader headcount query across a set of leaders (the selected
 * leader + their directs) and flattens every point into one array. Powers the
 * cross-leader Connection-vs-Attrition / Headcount-change quadrants on the
 * Insights tab. Per-leader failures are swallowed (best-effort) so one bad fetch
 * doesn't blank the whole view; the dep key is the sorted login list.
 */
export function useMultiLeaderHeadcount(
  leaders: Array<{ login: string; name: string }>,
  startDate?: string | null,
  endDate?: string | null
): AsyncState<ConnectionsHeadcountPoint[]> {
  const [state, setState] = useState<AsyncState<ConnectionsHeadcountPoint[]>>(DEFAULT_STATE());
  const { viewerLogin, isPoweruser } = useViewerContext();
  const key = leaders
    .map((l) => l.login)
    .sort()
    .join(',');

  useEffect(() => {
    if (leaders.length === 0) {
      setState({ data: [], loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });

    Promise.all(
      leaders.map((leader) =>
        fetchConnections<ConnectionsHeadcountPoint[]>(
          getConnectionsHeadcount,
          { leaderLogin: leader.login, startDate, endDate, viewerLogin, isPoweruser },
          'getConnectionsHeadcount'
        ).catch(() => [] as ConnectionsHeadcountPoint[])
      )
    )
      .then((perLeader) => {
        if (!cancelled) setState({ data: perLeader.flat(), loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, startDate, endDate, viewerLogin, isPoweruser]);

  return state;
}
